import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';

function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    || 'heading';
}

function stripTags(html) {
  return html.replace(/<[^>]*>/g, '');
}

function extractHeadings(html) {
  if (!html || typeof html !== 'string') return [];
  const headings = [];
  const seen = {};
  const regex = /<h([1-6])[^>]*>([\s\S]*?)<\/h[1-6]>/gi;
  let match;

  while ((match = regex.exec(html)) !== null) {
    const level = parseInt(match[1], 10);
    const text = stripTags(match[2]).trim();
    if (text) {
      let base = slugify(text);
      seen[base] = (seen[base] || 0) + 1;
      const id = seen[base] > 1 ? `${base}-${seen[base] - 1}` : base;
      headings.push({ level, text, id });
    }
  }

  return headings;
}

function useActiveHeading(headingIds) {
  const [activeId, setActiveId] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!headingIds.length) return;

    observerRef.current?.disconnect();

    const callback = (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) {
        setActiveId(visible[0].target.id);
      }
    };

    observerRef.current = new IntersectionObserver(callback, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    });

    for (const id of headingIds) {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [headingIds]);

  return activeId;
}

export const DocToc = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const body = evaluateTemplateValue({
    valueToEvaluate: props.content,
    globalDataContext,
    templateContext,
  });

  const headings = useMemo(() => extractHeadings(body), [body]);
  const headingIds = useMemo(() => headings.map((h) => h.id), [headings]);
  const activeId = useActiveHeading(headingIds);

  const handleClick = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (headings.length < 2) return null;

  return (
    <ActionDependant {...props}>
      <nav className="hidden lg:block sticky top-10" aria-label="Table des matières">
        <p className="text-2xs font-semibold text-stone-400 uppercase tracking-widest mb-4">
          Sur cette page
        </p>
        <ul className="space-y-0.5 text-xs border-l border-stone-200">
          {headings.map((h, i) => {
            const isActive = h.id === activeId;
            return (
              <li
                key={`${h.id}-${i}`}
                style={{ paddingLeft: `${(h.level - 1) * 12 + 12}px` }}
                className={isActive ? '-ml-px border-l-2 border-blue-500' : ''}
              >
                <a
                  href={`#${h.id}`}
                  onClick={(e) => handleClick(e, h.id)}
                  className={`block py-1 transition-colors truncate ${
                    isActive ? 'text-blue-600 font-medium' : 'text-stone-400 hover:text-blue-600'
                  }`}
                >
                  {h.text}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </ActionDependant>
  );
};
